import React, { useEffect } from 'react';
import { ModalOwnerState } from '@mui/material';
import { KEY_NAME } from 'web-shared/enums/key-name';
import { useTheme } from '@mui/material/styles';

interface ModalRootProps extends React.HTMLAttributes<HTMLDivElement> {
  ownerState: ModalOwnerState;
}

// disableEnforceFocus lets focus move into content portalled outside the modal root,
// so Escape never bubbles through the root's own onKeyDown handler. 
// That is why we listen for the Escape key on the document instead.
export const ModalRoot = React.forwardRef<HTMLDivElement, ModalRootProps>(({ ownerState, style, ...props }, ref) => {
  const theme = useTheme();
  const { open, exited, onClose, disableEscapeKeyDown } = ownerState;

  useEffect(() => {
    if (!open || disableEscapeKeyDown) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === KEY_NAME.ESC) {
        onClose?.(event, 'escapeKeyDown');
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, disableEscapeKeyDown, onClose]);

  return (
    <div
      ref={ref}
      {...props}
      style={{
        position: 'fixed',
        zIndex: theme.zIndex.modal,
        inset: 0,
        visibility: !open && exited ? 'hidden' : undefined,
        ...style,
      }}
    />
  );
});

ModalRoot.displayName = 'ModalRoot';
